import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  authenticateAdmin, 
  generateSessionToken, 
  isSessionValid, 
  secureStorage,
  type AdminAuthResult,
  type AdminUser
} from '../lib/adminAuth';

export function useAdminAuth() {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const isAuthenticated = secureStorage.getItem('authenticated');
      const userDataStr = secureStorage.getItem('adminUser');
      const loginTime = secureStorage.getItem('loginTime');
      
      if (isAuthenticated === 'true' && userDataStr && loginTime) {
        // Validate session timeout
        if (isSessionValid(loginTime)) {
          try {
            const userData = JSON.parse(userDataStr);
            setAdminUser(userData);
          } catch (parseError) {
            console.error('Error parsing admin user data:', parseError);
            logout();
          }
        } else {
          // Session expired, clear and redirect
          logout();
        }
      } else {
        setAdminUser(null);
      }
    } catch (error) {
      console.error('Error checking admin auth status:', error);
      setAdminUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ 
    success: boolean; 
    error?: string;
    attemptsRemaining?: number;
    remainingLockoutTime?: number;
  }> => {
    try {
      setIsLoading(true);
      
      // Authenticate using secure database method
      const result: AdminAuthResult = await authenticateAdmin(email, password);
      
      if (result.success && result.user) {
        // Generate secure session token
        const sessionToken = generateSessionToken();
        const loginTime = new Date().toISOString();
        
        // Store session securely
        secureStorage.setItem('authenticated', 'true');
        secureStorage.setItem('adminUser', JSON.stringify(result.user));
        secureStorage.setItem('loginTime', loginTime);
        secureStorage.setItem('sessionToken', sessionToken);
        
        setAdminUser(result.user);
        setLoginAttempts(0);
        setIsBlocked(false);
        setBlockTimeRemaining(0);
        
        return { success: true };
      } else {
        // Handle rate limiting and account lockout
        if (result.remainingLockoutTime && result.isLocked) {
          setIsBlocked(true);
          setBlockTimeRemaining(result.remainingLockoutTime);
          
          // Start countdown timer
          const timer = setInterval(() => {
            setBlockTimeRemaining(prev => {
              const newTime = prev - 1000;
              if (newTime <= 0) {
                setIsBlocked(false);
                clearInterval(timer);
                return 0;
              }
              return newTime;
            });
          }, 1000);
        }
        
        if (result.attemptsRemaining !== undefined) {
          setLoginAttempts(5 - result.attemptsRemaining);
        }
        
        return { 
          success: false, 
          error: result.error || 'Authentication failed',
          attemptsRemaining: result.attemptsRemaining,
          remainingLockoutTime: result.remainingLockoutTime
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Login failed. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      // Clear secure storage
      secureStorage.clear();
      
      setAdminUser(null);
      setLoginAttempts(0);
      setIsBlocked(false);
      setBlockTimeRemaining(0);
      
      // Only navigate if not already on login page
      const currentPath = window.location.pathname;
      if (currentPath !== '/admin/login') {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAuthenticated = (): boolean => {
    try {
      const isAuth = secureStorage.getItem('authenticated') === 'true';
      const adminUserStr = secureStorage.getItem('adminUser');
      const loginTime = secureStorage.getItem('loginTime');
      
      if (!isAuth || !adminUserStr || !loginTime) {
        return false;
      }

      // Check session validity
      if (!isSessionValid(loginTime)) {
        logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  };

  const requireAuth = () => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return false;
    }
    return true;
  };

  return {
    adminUser,
    isLoading,
    loginAttempts,
    isBlocked,
    blockTimeRemaining,
    login,
    logout,
    isAuthenticated,
    requireAuth,
    checkAuthStatus,
  };
}
